import { api } from '@/lib/api';

export type WhatsAppTemplateStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE';
export type WhatsAppCampaignStatus =
  | 'DRAFT'
  | 'SCHEDULED'
  | 'SENDING'
  | 'SENT'
  | 'STOPPED'
  | 'FAILED';
export type WhatsAppMessageStatus =
  | 'QUEUED'
  | 'SENT'
  | 'DELIVERED'
  | 'READ'
  | 'FAILED'
  | 'SKIPPED';

export type MetaTemplateButton = {
  type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER';
  text: string;
  url?: string;
  phoneNumber?: string;
  urlExample?: string;
};

export type CampaignCsvRecipient = {
  phone: string;
  patientName?: string;
  source?: string;
  campaignName?: string;
};

export type CampaignAudienceExclude = {
  statuses?: string[];
  subStatuses?: string[];
  phones?: string[];
};

export type CampaignAudience = {
  mode?: 'FILTER' | 'CSV';
  statuses?: string[];
  subStatuses?: string[];
  sources?: string[];
  campaignNames?: string[];
  assignedToIds?: string[];
  isSurgeryCase?: boolean;
  createdFrom?: string;
  createdTo?: string;
  csvRecipients?: CampaignCsvRecipient[];
  defaultSource?: string;
  createMissingLeads?: boolean;
  defaultCampaignName?: string;
  exclude?: CampaignAudienceExclude;
};

export type CampaignSendOptions = {
  headerType?: string;
  headerMediaLink?: string;
  headerMediaId?: string;
  libraryMediaId?: string;
  headerMediaFilename?: string;
  urlButtonParam?: string;
};

export interface WhatsAppTemplate {
  id: string;
  hospitalId: string;
  name: string;
  languageCode: string;
  category?: string | null;
  description?: string | null;
  bodyParamCount: number;
  metaSchema?: any;
  status: WhatsAppTemplateStatus;
  createdAt: string;
  updatedAt: string;
}

export type CampaignReportAssigneeBucket = {
  assignedToId: string | null;
  assignedToName: string;
  total: number;
  sent: number;
  delivered: number;
  read: number;
  replied: number;
  failed: number;
  skipped: number;
};

export type TemplateSentReportRow = {
  templateName: string;
  languageCode: string | null;
  status: string | null;
  category: string | null;
  campaignSent: number;
  campaignDelivered: number;
  campaignRead: number;
  campaignReplied: number;
  campaignFailed: number;
  campaignSkipped: number;
  notificationSent: number;
  notificationDelivered: number;
  notificationRead: number;
  notificationFailed: number;
  feedbackOpdSent: number;
  loggedFailures: number;
  totalSent: number;
  sources: Array<'campaign' | 'notification' | 'feedback_opd' | 'configured'>;
};

export type TemplateSentReport = {
  generatedAt: string;
  hospitalId: string;
  range: { from: string | null; to: string | null };
  totals: {
    templates: number;
    totalSent: number;
    campaignSent: number;
    notificationSent: number;
    feedbackOpdSent: number;
    loggedFailures: number;
  };
  rows: TemplateSentReportRow[];
  notes: string[];
};

export type CrmAutomationKind =
  | 'DNP_RESCHEDULE'
  | 'FOLLOW_UP_MISSED'
  | 'LOST_CHECK'
  | 'CAMPAIGN_WELCOME';

export type CrmAutomationReportRow = {
  automationKind: CrmAutomationKind;
  automationLabel: string;
  templateName: string;
  leadId: string;
  patientName: string;
  phone: string;
  source: string | null;
  campaignName: string | null;
  status: string | null;
  leadSubStatus: string | null;
  assignedToId: string | null;
  assignedToName: string | null;
  sentAt: string;
  delivered: boolean;
  deliveredAt: string | null;
  opened: boolean;
  readAt: string | null;
  replied: boolean;
  replyType: 'reschedule' | 'call_now' | 'lost_revival' | 'inbound' | null;
  repliedAt: string | null;
  replyDetail: string | null;
  customerCallbackAt: string | null;
  milestone: number | null;
  waMessageId: string | null;
  deliveryStatus: string | null;
  failed: boolean;
  error: string | null;
};

export type CrmAutomationBucket = {
  kind: CrmAutomationKind;
  label: string;
  templateName: string;
  sent: number;
  delivered: number;
  opened: number;
  replied: number;
  replyReschedule: number;
  replyCallNow: number;
  replyLostRevival: number;
  failed: number;
  pendingScheduled: number;
};

export type CrmAutomationWhatsAppReport = {
  generatedAt: string;
  hospitalId: string;
  range: { from: string | null; to: string | null };
  totals: {
    sent: number;
    delivered: number;
    opened: number;
    replied: number;
    failed: number;
    pendingScheduled: number;
  };
  byAutomation: CrmAutomationBucket[];
  rows: CrmAutomationReportRow[];
  notes: string[];
};

export type OpdFeedbackReportRow = {
  id: string;
  receivedAt: string;
  phone: string;
  leadId: string | null;
  patientName: string | null;
  templateName: string | null;
  surveyName: string | null;
  rating: number | null;
  ratingLabel: string | null;
  freeText: string | null;
  flowToken: string | null;
  contextMessageId: string | null;
  waMessageId: string;
};

export type OpdFeedbackReport = {
  generatedAt: string;
  hospitalId: string;
  range: { from: string | null; to: string | null };
  totals: {
    responses: number;
    withRating: number;
    avgRating: number | null;
    rating1: number;
    rating2: number;
    rating3: number;
    rating4: number;
    rating5: number;
  };
  rows: OpdFeedbackReportRow[];
};

export type FeedbackOpdAutomationConfig = {
  automationKey: 'feedback_opd';
  enabled: boolean;
  templateName: string;
  languageCode: string | null;
  sendFlowToken: boolean;
  headerMedia: {
    id: string;
    publicUrl: string;
    fileName: string;
    mediaCategory: string;
  } | null;
  effective: {
    templateName: string;
    languageCode: string | null;
    source: 'hospital' | 'env' | 'default';
    headerSource: 'hospital' | 'template_default' | 'env' | 'none';
  };
  updatedAt: string | null;
};

export type CampaignReportSummary = {
  campaignId: string;
  campaignName: string;
  templateName: string | null;
  campaignStatus: string;
  startedAt: string | null;
  finishedAt: string | null;
  totals: {
    total: number;
    sent: number;
    delivered: number;
    read: number;
    replied: number;
    failed: number;
    skipped: number;
  };
  rates: {
    deliveryPct: number | null;
    readPct: number | null;
    replyPct: number | null;
  };
  byAssignee: CampaignReportAssigneeBucket[];
};

export type CampaignReportRow = {
  id: string;
  phone: string;
  patientName: string | null;
  leadId: string | null;
  leadStatus: string | null;
  leadSubStatus: string | null;
  leadSource: string | null;
  assignedToId: string | null;
  assignedToName: string | null;
  status: WhatsAppMessageStatus;
  sent: boolean;
  delivered: boolean;
  opened: boolean;
  replied: boolean;
  failed: boolean;
  skipped: boolean;
  sentAt: string | null;
  deliveredAt: string | null;
  readAt: string | null;
  repliedAt: string | null;
  error: string | null;
};

export interface WhatsAppCampaign {
  id: string;
  hospitalId: string;
  name: string;
  templateId?: string | null;
  textBody?: string | null;
  bodyParams?: any;
  audience?: CampaignAudience | null;
  sendOptions?: CampaignSendOptions | null;
  status: WhatsAppCampaignStatus;
  scheduledAt?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  template?: WhatsAppTemplate | null;
  _count?: { messages: number };
}

export interface WhatsAppButtonResponseRule {
  id: string;
  hospitalId: string;
  name: string;
  buttonLabel: string;
  leadStatus?: string | null;
  leadSubStatus?: string | null;
  isActive: boolean;
  priority: number;
}

function parseButtonsCsv(buttonsCsv: string): MetaTemplateButton[] {
  return String(buttonsCsv || '')
    .split(/[,;\n]+/)
    .map((x) => x.trim())
    .filter(Boolean)
    .slice(0, 3)
    .map((text) => ({ type: 'QUICK_REPLY' as const, text: text.slice(0, 25) }));
}

export const whatsappCampaignsApi = {
  listTemplates: () => api.get('/api/whatsapp-campaigns/templates'),
  createTemplate: (payload: Partial<WhatsAppTemplate>) =>
    api.post('/api/whatsapp-campaigns/templates', payload),
  updateTemplate: (id: string, payload: Partial<WhatsAppTemplate>) =>
    api.patch(`/api/whatsapp-campaigns/templates/${id}`, payload),
  syncTemplatesFromMeta: () => api.post('/api/whatsapp-campaigns/templates/sync-meta', {}),

  /** Server downloads from URL — use for large videos when nginx returns 413 */
  uploadTemplateMediaFromUrl: (url: string, headerType?: string) =>
    api.post('/api/whatsapp-campaigns/templates/upload-media-from-url', { url, headerType }),

  uploadTemplateMedia: async (file: File) => {
    const maxJsonBytes = 4 * 1024 * 1024;
    if (file.size <= maxJsonBytes) {
      const mediaBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const raw = String(reader.result || '');
          const comma = raw.indexOf(',');
          resolve(comma >= 0 ? raw.slice(comma + 1) : raw);
        };
        reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });
      return api.post('/api/whatsapp-campaigns/templates/upload-media-json', {
        mediaBase64,
        filename: file.name,
        mimetype: file.type || 'application/octet-stream',
      });
    }
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/api/whatsapp-campaigns/templates/upload-media', fd);
  },

  createMetaTemplate: (payload: {
    name: string;
    languageCode?: string;
    category?: string;
    bodyText: string;
    bodyParamCount?: number;
    headerType?: string;
    headerText?: string;
    headerMediaHandle?: string;
    footerText?: string;
    buttonsCsv?: string;
    buttons?: MetaTemplateButton[];
  }) => {
    const buttons = payload.buttons?.length
      ? payload.buttons
      : payload.buttonsCsv
        ? parseButtonsCsv(payload.buttonsCsv)
        : undefined;
    const { buttonsCsv: _b, ...rest } = payload;
    return api.post('/api/whatsapp-campaigns/templates/create-meta', { ...rest, buttons });
  },

  createMetaTemplateAndSync: (payload: {
    name: string;
    languageCode?: string;
    category?: string;
    bodyText: string;
    bodyParamCount?: number;
    headerType?: string;
    headerText?: string;
    headerMediaHandle?: string;
    footerText?: string;
    buttonsCsv?: string;
    buttons?: MetaTemplateButton[];
  }) => {
    const buttons = payload.buttons?.length
      ? payload.buttons
      : payload.buttonsCsv
        ? parseButtonsCsv(payload.buttonsCsv)
        : undefined;
    const { buttonsCsv: _b, ...rest } = payload;
    return api.post('/api/whatsapp-campaigns/templates/create-meta-and-sync', { ...rest, buttons });
  },

  listButtonResponseRules: () => api.get('/api/whatsapp-campaigns/button-response-rules'),
  createButtonResponseRule: (payload: Partial<WhatsAppButtonResponseRule>) =>
    api.post('/api/whatsapp-campaigns/button-response-rules', payload),
  updateButtonResponseRule: (id: string, payload: Partial<WhatsAppButtonResponseRule>) =>
    api.patch(`/api/whatsapp-campaigns/button-response-rules/${id}`, payload),
  deleteButtonResponseRule: (id: string) =>
    api.delete(`/api/whatsapp-campaigns/button-response-rules/${id}`),
  seedButtonResponseRules: () =>
    api.post('/api/whatsapp-campaigns/button-response-rules/seed-defaults', {}),

  downloadAudienceCsvTemplate: () =>
    api.get('/api/whatsapp-campaigns/campaigns/audience-csv-template', { responseType: 'blob' }),

  /** Read CSV in browser and POST as JSON (reliable behind nginx; no multipart). */
  parseAudienceCsv: async (file: File) => {
    const csvText = await file.text();
    return api.post('/api/whatsapp-campaigns/campaigns/parse-audience-text', { csvText });
  },

  listCampaigns: () => api.get('/api/whatsapp-campaigns/campaigns'),
  createCampaign: (payload: Partial<WhatsAppCampaign>) =>
    api.post('/api/whatsapp-campaigns/campaigns', payload),
  getCampaign: (id: string) => api.get(`/api/whatsapp-campaigns/campaigns/${id}`),
  previewAudience: (id: string) => api.post(`/api/whatsapp-campaigns/campaigns/${id}/preview`, {}),
  sendNow: (id: string, limit = 5000, sendOptions?: CampaignSendOptions) =>
    api.post(
      `/api/whatsapp-campaigns/campaigns/${id}/send`,
      {
        limit,
        ...(sendOptions && Object.keys(sendOptions).length ? { sendOptions } : {}),
      },
      { timeout: 60_000 }
    ),

  /** Clear orphaned SENDING after API restart so Send now works again. */
  unstickCampaign: (id: string) =>
    api.post(`/api/whatsapp-campaigns/campaigns/${id}/unstick`, { force: true }),

  listMessages: (id: string, params: { page?: number; pageSize?: number; status?: WhatsAppMessageStatus } = {}) =>
    api.get(`/api/whatsapp-campaigns/campaigns/${id}/messages`, { params }),

  getCampaignReport: (
    id: string,
    params: { page?: number; pageSize?: number; assignedToId?: string } = {}
  ) => api.get(`/api/whatsapp-campaigns/campaigns/${id}/report`, { params }),

  downloadCampaignReportCsv: (id: string, assignedToId?: string) =>
    api.get(`/api/whatsapp-campaigns/campaigns/${id}/report/export`, {
      params: assignedToId ? { assignedToId } : {},
      responseType: 'blob',
    }),

  /** Hospital-wide send counts by template name (campaigns + notifications + feedback_opd). */
  getTemplateSentReport: (params: { from?: string; to?: string } = {}) =>
    api.get('/api/whatsapp-campaigns/templates/sent-report', { params }),

  downloadTemplateSentReportCsv: (params: { from?: string; to?: string } = {}) =>
    api.get('/api/whatsapp-campaigns/templates/sent-report/export', {
      params,
      responseType: 'blob',
    }),

  /** DNP / missed follow-up / lost_check CRM automation report. */
  getCrmAutomationReport: (params: { from?: string; to?: string } = {}) =>
    api.get('/api/whatsapp-campaigns/automations/crm-report', { params }),

  downloadCrmAutomationReportCsv: (params: { from?: string; to?: string } = {}) =>
    api.get('/api/whatsapp-campaigns/automations/crm-report/export', {
      params,
      responseType: 'blob',
    }),

  /** OPD Flow survey responses (ratings + free text). */
  getOpdFeedbackReport: (params: { from?: string; to?: string } = {}) =>
    api.get('/api/whatsapp-campaigns/feedback/opd-report', { params }),

  downloadOpdFeedbackReportCsv: (params: { from?: string; to?: string } = {}) =>
    api.get('/api/whatsapp-campaigns/feedback/opd-report/export', {
      params,
      responseType: 'blob',
    }),

  getFeedbackOpdAutomation: () =>
    api.get('/api/whatsapp-campaigns/automations/feedback-opd'),

  saveFeedbackOpdAutomation: (payload: {
    enabled?: boolean;
    templateName?: string | null;
    languageCode?: string | null;
    headerMediaId?: string | null;
    sendFlowToken?: boolean;
    clearHeaderMedia?: boolean;
  }) => api.put('/api/whatsapp-campaigns/automations/feedback-opd', payload),

  /** Preview or delete NEW WHATSAPP_CAMPAIGN leads auto-created from CSV send with no reply/button. */
  cleanupAutoLeads: (payload: { day?: string; apply?: boolean; campaignName?: string } = {}) =>
    api.post('/api/whatsapp-campaigns/campaigns/cleanup-auto-leads', payload, { timeout: 180_000 }),
};
